import {
  platforms_tiktok_oauth_shopify_redirect,
  VITE_API_URL,
} from "@/constants";
import { tiktokAuth } from "@/hooks/tiktok-mutation";
import { useAuth } from "@/providers/auth";
import { useOrg } from "@/providers/organization";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Authorizations started in the embedded Shopify app carry a state. Their sellers
// aren't signed in here, so they're sent back to the app to finish connecting
async function getShopifyRedirect(code: string, state: string) {
  const res = await fetch(
    `${VITE_API_URL}${platforms_tiktok_oauth_shopify_redirect}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, state }),
    },
  );

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.url) {
    throw new Error(data.msg || i18n.t("auth.returnToShopifyFailed"));
  }

  return data.url as string;
}

export const Route = createFileRoute("/auth/tiktok/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const hasRun = useRef(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // Get URL parameters from current location
    const urlParams = new URLSearchParams(window.location.search);
    const appKey = urlParams.get("app_key");
    const code = urlParams.get("code");
    const locale = urlParams.get("locale");
    const shopRegion = urlParams.get("shop_region");
    const state = urlParams.get("state");

    if (state) {
      if (!code) {
        setErrorMessage(t("auth.tiktok.noCode"));
        return;
      }

      getShopifyRedirect(code, state)
        .then((url) => window.location.replace(url))
        .catch((error) => {
          console.error("TikTok OAuth Shopify redirect error:", error);
          setErrorMessage(
            error instanceof Error ? error.message : t("common.networkError"),
          );
        });
      return;
    }

    // Validate required parameters
    if (!appKey || !code || !locale || !shopRegion) {
      console.error("Missing required tiktok auth parameters");
      window.location.href = "/auth/login";
      return;
    }

    // Call the async function
    tiktokAuth(code, getToken, orgId, navigate);
  }, [getToken, navigate]);

  if (errorMessage) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-4">
          <h2 className="text-xl font-semibold mb-2">
            {t("auth.connectionFailed")}
          </h2>
          <p className="text-muted-foreground mb-4">{errorMessage}</p>
          <p className="text-sm text-muted-foreground">
            {t("auth.tiktok.tryAgain")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
        <p>{t("auth.tiktok.connecting")}</p>
      </div>
    </div>
  );
}
